import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { apiService } from '../services/api';
import { AuthShell } from './Login';

interface ForgotPasswordForm {
  email: string;
}

export function ForgotPassword() {
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setError('');
      await apiService.forgotPassword(data.email);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  if (submitted) {
    return (
      <AuthShell>
        <div className="space-y-2 mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Check your email</h1>
          <p className="text-sm text-muted-foreground">
            If an account exists for that email, we've sent a link to reset your password. The link expires in 1 hour.
          </p>
        </div>
        <Link
          to="/login"
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 flex items-center justify-center"
        >
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Reset your password</h1>
        <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="email">Email</label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
            })}
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-40 mt-2"
        >
          {isSubmitting ? 'Sending…' : 'Send reset link'}
        </button>

        <Link
          to="/login"
          className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Back to sign in
        </Link>
      </form>
    </AuthShell>
  );
}
